/**
 * Organiser sign in and sign up: the pure rules the screens share.
 *
 * Kept out of the components so they can be tested on their own: the email
 * check, the code field's cleaning, the password rule, where to send someone
 * after they sign in, and Clerk's error codes turned into plain words.
 */

export const PASSWORD_MIN_LENGTH = 8; // matches Clerk's production setting (8)
export const CODE_LENGTH = 6;

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * The email step's own check, before anything is sent. Returns the message to
 * show under the field, or null when it is fine.
 */
export function emailFieldError(value: string): string | null {
  if (!value.trim()) return 'Enter your email address.';
  if (!isValidEmail(value)) return "That doesn't look like an email address. Check for a typo.";
  return null;
}

/** Digits only, at most six. Paste works: everything else is dropped. */
export function sanitiseCode(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, CODE_LENGTH);
}

export function isCompleteCode(code: string): boolean {
  return new RegExp(`^\\d{${CODE_LENGTH}}$`).test(code);
}

export function passwordRuleMet(password: string): boolean {
  return password.length >= PASSWORD_MIN_LENGTH;
}

/**
 * Where to go after signing in. Honours a same-site `redirect_url` (Clerk
 * passes an absolute URL there) and refuses anything that leaves the site or
 * loops back into sign in or sign up. Null means "use the default".
 */
export function safeRedirectPath(raw: string | null | undefined, origin: string): string | null {
  if (!raw) return null;
  let path: string;
  if (raw.startsWith('/') && !raw.startsWith('//') && !raw.startsWith('/\\')) {
    path = raw;
  } else {
    try {
      const url = new URL(raw);
      if (url.origin !== origin) return null;
      path = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }
  if (/^\/(sign-in|sign-up)(\/|\?|#|$)/.test(path)) return null;
  return path;
}

/* ── Clerk errors in plain words ───────────────────────────────────────── */

export type AuthErrorContext = 'email' | 'password' | 'code' | 'new-password' | 'provider' | 'signup-email';

/**
 * Clerk's error code, turned into something a person can act on. Clerk's own
 * messages are written for developers ("Identifier is invalid") and can name
 * Clerk, so none of them reach the screen.
 */
export function authErrorMessage(code: string | null | undefined, context: AuthErrorContext, status?: number): string | null {
  if (status === 429 || code === 'too_many_requests' || code === 'rate_limit_exceeded') {
    return 'Too many tries in a row. Wait a minute, then try again.';
  }
  switch (code) {
    case 'form_identifier_not_found':
    case 'invitation_account_not_exists':
      return "There's no Fitvent account with that email. Check for a typo, or create an account.";
    case 'form_identifier_exists':
    case 'identifier_already_exists':
      return "There's already an account with that email. Sign in instead.";
    case 'form_param_format_invalid':
    case 'form_param_value_invalid':
      if (context === 'email' || context === 'signup-email') {
        return "That doesn't look like an email address. Check for a typo.";
      }
      break;
    case 'form_password_incorrect':
      return "That password isn't right. Try again, or use Forgot it?";
    case 'form_password_pwned':
    case 'form_password_pwned__sign_in':
      return 'That password has turned up in a data breach on another site. Choose a different one.';
    case 'form_password_length_too_short':
      return `Use at least ${PASSWORD_MIN_LENGTH} characters.`;
    case 'form_password_not_strong_enough':
    case 'form_password_validation_failed':
      return 'Choose a stronger password: a longer one, or one with a few words in it.';
    case 'form_password_size_in_bytes_exceeded':
      return 'That password is too long. Use a shorter one.';
    case 'form_code_incorrect':
      return "That code isn't right. Check the email and try again.";
    case 'verification_expired':
      return 'That code has expired. Send a new one.';
    case 'verification_failed':
      return 'Too many tries with that code. Send a new one.';
    case 'strategy_for_user_invalid':
      return context === 'code'
        ? "Email codes aren't switched on for signing in yet. Use your password for now."
        : "That way of signing in isn't set up for this account. Use your password or an email code.";
    case 'session_exists':
    case 'identifier_already_signed_in':
      return "You're already signed in.";
    case 'feature_not_enabled':
      return "That isn't switched on yet. Use your email instead.";
    case 'oauth_access_denied':
      return 'Sign in was cancelled. Try again, or use your email.';
    case 'external_account_exists':
    case 'oauth_email_domain_reserved_by_saml':
      return 'That account is already linked to a different sign in. Use your email instead.';
    case 'not_allowed_access':
    case 'sign_up_mode_restricted':
      return "We can't create an account with that email right now. Talk to our team.";
    default:
      break;
  }
  if (context === 'provider') return "That didn't work. Try again, or use your email.";
  return 'Something went wrong. Try again in a moment.';
}

/** Pull Clerk's error code and status out of whatever was thrown. */
export function clerkErrorCode(err: unknown): { code: string | null; status?: number } {
  if (!err || typeof err !== 'object') return { code: null };
  const e = err as { errors?: Array<{ code?: string }>; code?: string; status?: number };
  const code = e.errors?.[0]?.code ?? (typeof e.code === 'string' ? e.code : null);
  return { code, status: typeof e.status === 'number' ? e.status : undefined };
}
